import DOMPurify from "dompurify";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Download,
  Edit3,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  Image,
  Italic,
  Link,
  List,
  ListOrdered,
  Plus,
  Quote,
  Save,
  Trash2,
  Underline,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  showPrompt,
  showToastError,
  showToastSuccess,
} from "../../shared/alertService";
import apiService, { API_ENDPOINTS } from "../../shared/apiService";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";

interface EbookChapter {
  chapter_id: string;
  title: string;
  content: string;
  order: number;
  word_count: number;
  estimated_read_time: number;
  key_points: string[];
}

interface EbookEditorProps {
  ebook: {
    ebook_id: string;
    title: string;
    subtitle?: string;
    description: string;
    chapters?: EbookChapter[];
    author?: string;
    company?: string;
    target_audience?: string;
    keywords?: string[];
    estimated_read_time?: number;
    word_count?: number;
    template?: string;
    status?: string;
    created_at?: string;
    published_at?: string;
    download_count?: number;
    lead_count?: number;
  };
  onSave: (updatedEbook: any) => void;
  onClose: () => void;
  isOpen: boolean;
}

const EbookEditor: React.FC<EbookEditorProps> = ({
  ebook,
  onSave,
  onClose,
  isOpen,
}) => {
  const [editedEbook, setEditedEbook] = useState(ebook);
  const [activeChapter, setActiveChapter] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Vira `true` na primeira tentativa de salvar com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [attemptedSave, setAttemptedSave] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditedEbook(ebook);

    // Garantir que sempre tenha pelo menos um capítulo
    if (!ebook.chapters || ebook.chapters.length === 0) {
      const defaultChapter: EbookChapter = {
        chapter_id: `chapter_${Date.now()}`,
        title: "Introdução",
        content: "",
        order: 1,
        word_count: 0,
        estimated_read_time: 0,
        key_points: [],
      };

      setEditedEbook({
        ...ebook,
        chapters: [defaultChapter],
      });
    }
  }, [ebook]);

  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToastError("Por favor, selecione apenas arquivos de imagem.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB
      showToastError("A imagem deve ter no máximo 5MB.");
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiService.uploadFile(
        API_ENDPOINTS.IMAGES.UPLOAD,
        file,
      );

      if (!response.success) {
        throw new Error("Erro ao fazer upload da imagem");
      }

      const data = await response.json();

      // Inserir imagem no editor
      const img = `<img src="${data.url}" alt="${file.name}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      formatText("insertHTML", img);

      showToastSuccess("Imagem inserida com sucesso!");
    } catch (err) {
      showToastError("Erro ao fazer upload da imagem. Tente novamente.");
    } finally {
      setUploadingImage(false);
    }
  };

  const updateChapterContent = (chapterIndex: number, content: string) => {
    const chapters = editedEbook.chapters || [];
    const updatedChapters = [...chapters];
    updatedChapters[chapterIndex] = {
      ...updatedChapters[chapterIndex],
      content,
      word_count: content.split(" ").length,
    };

    setEditedEbook({
      ...editedEbook,
      chapters: updatedChapters,
      word_count: updatedChapters.reduce(
        (total, chapter) => total + chapter.word_count,
        0,
      ),
    });
  };

  const addChapter = () => {
    const chapters = editedEbook.chapters || [];
    const newChapter: EbookChapter = {
      chapter_id: `chapter_${Date.now()}`,
      title: "Novo Capítulo",
      content: "",
      order: chapters.length + 1,
      word_count: 0,
      estimated_read_time: 0,
      key_points: [],
    };

    setEditedEbook({
      ...editedEbook,
      chapters: [...chapters, newChapter],
    });
    setActiveChapter(chapters.length);
  };

  const removeChapter = (chapterIndex: number) => {
    const chapters = editedEbook.chapters || [];
    if (chapters.length <= 1) {
      showToastError("O ebook deve ter pelo menos um capítulo.");
      return;
    }

    const updatedChapters = chapters.filter(
      (_, index) => index !== chapterIndex,
    );
    setEditedEbook({
      ...editedEbook,
      chapters: updatedChapters,
    });

    if (activeChapter >= updatedChapters.length) {
      setActiveChapter(updatedChapters.length - 1);
    }
  };

  const handleSave = async () => {
    setAttemptedSave(true);
    if (!editedEbook.title?.trim()) {
      showToastError("O título do ebook é obrigatório.");
      return;
    }
    try {
      await onSave(editedEbook);
      showToastSuccess("Ebook salvo com sucesso!");
      setAttemptedSave(false);
    } catch (err) {
      showToastError("Erro ao salvar o ebook. Tente novamente.");
    }
  };

  const renderToolbar = () => (
    <div className="flex items-center gap-1 sm:gap-2 p-2 sm:p-3 bg-purple-50/50 border-b border-purple-200 overflow-x-auto custom-scrollbar">
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="secondary"
          icon={<Bold className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("bold")}
          title="Negrito"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Italic className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("italic")}
          title="Itálico"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Underline className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("underline")}
          title="Sublinhado"
          className="p-1.5 sm:p-2"
        />
      </div>

      <div className="w-px h-5 sm:h-6 bg-purple-200 shrink-0" />

      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="secondary"
          icon={<Heading1 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("formatBlock", "h1")}
          title="Título 1"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Heading2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("formatBlock", "h2")}
          title="Título 2"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Heading3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("formatBlock", "h3")}
          title="Título 3"
          className="p-1.5 sm:p-2"
        />
      </div>

      <div className="w-px h-5 sm:h-6 bg-purple-300 shrink-0" />

      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="secondary"
          icon={<List className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("insertUnorderedList")}
          title="Lista com marcadores"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<ListOrdered className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("insertOrderedList")}
          title="Lista numerada"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Quote className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("formatBlock", "blockquote")}
          title="Citação"
          className="p-1.5 sm:p-2"
        />
      </div>

      <div className="hidden sm:block w-px h-5 sm:h-6 bg-purple-300 shrink-0" />

      <div className="hidden sm:flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="secondary"
          icon={<AlignLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("justifyLeft")}
          title="Alinhar à esquerda"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<AlignCenter className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("justifyCenter")}
          title="Centralizar"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<AlignRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("justifyRight")}
          title="Alinhar à direita"
          className="p-1.5 sm:p-2"
        />
      </div>

      <div className="w-px h-5 sm:h-6 bg-purple-300 shrink-0" />

      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="secondary"
          icon={<Link className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={async () => {
            const url = await showPrompt(
              "Digite a URL:",
              "Inserir link",
              "https://exemplo.com",
              "",
            );
            if (url) formatText("createLink", url);
          }}
          title="Inserir link"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Image className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={insertImage}
          disabled={uploadingImage}
          title="Inserir imagem"
          className="p-1.5 sm:p-2"
        />
        <Button
          size="sm"
          variant="secondary"
          icon={<Code className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
          onClick={() => formatText("formatBlock", "pre")}
          title="Código"
          className="p-1.5 sm:p-2"
        />
      </div>
    </div>
  );

  const renderEbookInfo = () => (
    <div className="w-full p-4 sm:p-5 bg-purple-50/50 border-b border-purple-200">
      <h3 className="font-semibold text-gray-900 mb-4 text-base sm:text-lg">
        1. Informações do Ebook
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            Título <span className="text-red-400">*</span>
          </label>
          <Input
            type="text"
            value={editedEbook.title}
            onChange={(e) =>
              setEditedEbook({ ...editedEbook, title: e.target.value })
            }
            error={
              attemptedSave && !editedEbook.title?.trim()
                ? "O título é obrigatório"
                : undefined
            }
            inputSize="sm"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            Subtítulo (opcional)
          </label>
          <Input
            type="text"
            value={editedEbook.subtitle}
            onChange={(e) =>
              setEditedEbook({ ...editedEbook, subtitle: e.target.value })
            }
            inputSize="sm"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            Autor (opcional)
          </label>
          <Input
            type="text"
            value={editedEbook.author}
            onChange={(e) =>
              setEditedEbook({ ...editedEbook, author: e.target.value })
            }
            inputSize="sm"
          />
        </div>
      </div>

      {/* Estatísticas */}
      <div className="mt-4 pt-4 border-t border-purple-200">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">
              Total de palavras:
            </span>
            <span className="font-semibold text-gray-900">
              {editedEbook.word_count}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Tempo de leitura:</span>
            <span className="font-semibold text-gray-900">
              {Math.max(1, Math.ceil((editedEbook.word_count || 0) / 200))} min
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Capítulos:</span>
            <span className="font-semibold text-gray-900">
              {editedEbook.chapters?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderChapters = () => (
    <div className="w-full p-4 sm:p-5 bg-white border-b border-purple-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
          2. Capítulos
        </h3>
        <Button
          size="sm"
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={addChapter}
          title="Adicionar capítulo"
        >
          Adicionar Capítulo
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
        {(editedEbook.chapters || []).map((chapter, index) => (
          <div
            key={chapter.chapter_id}
            className={`p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
              activeChapter === index
                ? "bg-purple-50 border-purple-400 shadow-md"
                : "bg-gray-50 border-gray-200 hover:bg-purple-50/50 hover:border-purple-300"
            }`}
            onClick={() => setActiveChapter(index)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-gray-500">
                    #{index + 1}
                  </span>
                  <h4 className="font-medium text-sm text-gray-900 truncate">
                    {chapter.title || "Sem título"}
                  </h4>
                </div>
                <p className="text-xs text-gray-500">
                  {chapter.word_count} palavras •{" "}
                  {Math.max(1, Math.ceil(chapter.word_count / 200))} min
                </p>
              </div>
              {(editedEbook.chapters || []).length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChapter(index);
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm transition-colors shrink-0"
                  title="Excluir capítulo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderEditor = () => {
    const chapters = editedEbook.chapters || [];
    const currentChapter = chapters[activeChapter];

    if (!currentChapter) {
      return (
        <div className="p-8 text-center text-gray-500">
          <p>
            Nenhum capítulo selecionado. Adicione um capítulo para começar a
            editar.
          </p>
        </div>
      );
    }

    return (
      <div className="w-full flex-1 flex flex-col min-w-0 bg-white">
        <div className="w-full p-4 sm:p-5 border-b border-purple-300">
          <h3 className="font-semibold text-gray-900 mb-3 text-base sm:text-lg">
            3. Editor de Texto
          </h3>
          <Input
            type="text"
            value={currentChapter.title || ""}
            onChange={(e) => {
              const updatedChapters = [...chapters];
              updatedChapters[activeChapter] = {
                ...updatedChapters[activeChapter],
                title: e.target.value,
              };
              setEditedEbook({ ...editedEbook, chapters: updatedChapters });
            }}
            inputSize="sm"
            className="text-lg sm:text-xl font-semibold"
            placeholder="Título do capítulo..."
          />
        </div>

        <div className="w-full">{renderToolbar()}</div>

        <div className="w-full flex-1 p-4 sm:p-5 min-h-0 overflow-auto">
          <div
            ref={editorRef}
            contentEditable
            className="w-full h-full min-h-[400px] sm:min-h-[500px] p-4 sm:p-6 border border-purple-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-purple-500 prose max-w-none text-gray-900 bg-white"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(currentChapter.content || ""),
            }}
            onInput={(e) => {
              const content = e.currentTarget.innerHTML;
              updateChapterContent(activeChapter, content);
            }}
          />
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    const chapters = editedEbook.chapters || [];
    const currentChapter = chapters[activeChapter];

    return (
      <div className="w-full flex-1 p-4 sm:p-6 bg-white overflow-y-auto custom-scrollbar">
        <div className="w-full mb-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-base sm:text-lg">
            3. Visualização
          </h3>
        </div>
        <div className="w-full">
          <div className="prose prose-sm sm:prose-lg max-w-none w-full">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {editedEbook.title}
            </h1>
            <h2 className="text-lg sm:text-xl text-gray-600 mb-4 sm:mb-6">
              {editedEbook.subtitle}
            </h2>

            <div className="border-t border-b border-purple-300 py-3 sm:py-4 my-4 sm:my-6">
              <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-500 gap-2 sm:gap-4">
                {editedEbook.author && <span>Por {editedEbook.author}</span>}
                {editedEbook.author && (
                  <span className="hidden sm:inline">•</span>
                )}
                <span>{editedEbook.word_count || 0} palavras</span>
                <span className="hidden sm:inline">•</span>
                <span>
                  {Math.max(1, Math.ceil((editedEbook.word_count || 0) / 200))}{" "}
                  min de leitura
                </span>
              </div>
            </div>

            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">
              {currentChapter?.title}
            </h2>

            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(currentChapter?.content || ""),
              }}
              className="text-gray-700 leading-relaxed text-sm sm:text-base"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Editor de Ebook"
        subtitle={editedEbook.title}
        icon={<Edit3 className="h-5 w-5" />}
        headerVariant="gradient"
        maxWidth="max-w-7xl"
        footer={
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={showPreview ? "secondary" : "primary"}
                icon={<Edit3 className="h-4 w-4" />}
                onClick={() => setShowPreview(false)}
              >
                Editar
              </Button>
              <Button
                size="sm"
                variant={showPreview ? "primary" : "secondary"}
                icon={<Eye className="h-4 w-4" />}
                onClick={() => setShowPreview(true)}
              >
                Visualizar
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<Download className="h-4 w-4" />}
                onClick={async () => {
                  try {
                    await apiService.downloadFile(
                      API_ENDPOINTS.EBOOKS.DOWNLOAD.replace(
                        "{id}",
                        ebook.ebook_id,
                      ),
                      `${editedEbook.title || "ebook"}-${ebook.ebook_id}.pdf`,
                    );
                    showToastSuccess("Download iniciado!");
                  } catch (err: any) {
                    showToastError(
                      err.message ||
                        "Erro ao baixar ebook. Verifique se o PDF foi gerado.",
                    );
                  }
                }}
                title="Baixar PDF"
              >
                Baixar PDF
              </Button>
              <Button
                size="sm"
                variant="primary"
                icon={<Save className="h-4 w-4" />}
                onClick={handleSave}
                className="shadow-lg shadow-purple-500/20"
              >
                Salvar
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex flex-col h-full w-full">
          {/* 1. Informações do Ebook */}
          {renderEbookInfo()}

          {/* 2. Capítulos */}
          {renderChapters()}

          {/* 3. Editor de Texto ou Preview */}
          <div className="w-full flex-1 min-h-0 overflow-hidden">
            {showPreview ? renderPreview() : renderEditor()}
          </div>
        </div>
      </Modal>

      {/* Input oculto para upload de imagens */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
    </>
  );
};

export default EbookEditor;
